import React from 'react';
import { Grid } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DyanamicDropdown from 'components/Elements/DyamicDropdown';
import { useFilters } from '../FilterContext';
import { useDepartments } from './hooks/useDepartments';
import { useFilterInitialization } from './hooks/useFilterInitialization';
import { SHOW_ALL_OPTION } from '../constants';

export const DepartmentFilter = () => {
  const { filterValues, updateFilter, isShowAll, config, initialValues } = useFilters();

  const {
    siteAppID,
    site: { siteDepartmentID, siteDepartmentName } = {},
    customCaptions
  } = JSON.parse(localStorage.getItem("me")) ||
      JSON.parse(sessionStorage.getItem("me")) ||
      {};

  const multiSelect = config.filters?.department?.multiSelect || false;
  const filterKey = 'department';
  const departments = useDepartments(siteAppID, isShowAll(filterKey));

  useFilterInitialization(filterKey, departments, {
    waitForData: true,
    persistAll: true,
    multiSelect,
    getDefaultValue: () => {
      // Check if 'all' is stored in initialValues for multi-select
      const storedValue = initialValues?.department;

      if (multiSelect) {
        // If 'all' is stored, use the show all option
        if (storedValue === 'all') {
          return [SHOW_ALL_OPTION];
        }
        // If array is stored (from URL or localStorage), let useFilterInitialization handle it
        if (Array.isArray(storedValue) && storedValue.length > 0) {
          return []; // Return empty array to let useFilterInitialization restore from stored IDs
        }
        // Only use default when there's truly NO stored value
        const userDepartment = siteDepartmentID ? { id: siteDepartmentID, name: siteDepartmentName } : null;
        return userDepartment ? [userDepartment] : [SHOW_ALL_OPTION];
      } else {
        // For single select, handle 'all' the same way
        if (storedValue === 'all') {
          return SHOW_ALL_OPTION;
        }
        // If there's a stored value, let useFilterInitialization handle it
        if (storedValue) {
          return null; // Return null to let useFilterInitialization restore from stored ID
        }
        // Only use default when there's truly NO stored value
        const userDepartment = siteDepartmentID ? { id: siteDepartmentID, name: siteDepartmentName } : null;
        return userDepartment || SHOW_ALL_OPTION;
      }
    },
    getParams: (value) => {
      if (multiSelect) {
        if (!value || value.length === 0) return {};
        // Check if Show All is selected (empty id)
        const hasShowAll = value.some(dept => dept.id === '' || dept.id === 'all');
        if (hasShowAll) {
          // If Show All is selected, pass empty params for API call
          return {};
        }
        const departmentIds = value.map(dept => dept.id).filter(id => id !== '' && id != null);
        return departmentIds.length === 0 ? {} : { siteDepartmentIds: departmentIds };
      }
      return value?.id && value.id !== '' ? { siteDepartmentId: value.id } : {};
    }
  });

  const handleChange = (selection) => {
    const params = multiSelect
      ? (() => {
          // Check if Show All is selected (empty id)
          const hasShowAll = selection.some(dept => dept.id === '' || dept.id === 'all');
          if (hasShowAll) {
            // If Show All is selected, pass empty params for API call
            return {};
          }
          const departmentIds = selection.map(dept => dept.id).filter(id => id !== '' && id != null);
          return departmentIds.length === 0 ? {} : { siteDepartmentIds: departmentIds };
        })()
      : (selection?.id && selection.id !== '' ? { siteDepartmentId: selection.id } : {});

    updateFilter(filterKey, selection, params, true);
  };

  const getPlaceholder = () => {
    if (!multiSelect) return `Select ${customCaptions?.department || 'Department'}`;
    const selected = filterValues[filterKey]?.value || [];
    return selected.length === 0
      ? 'Show All'
      : `${selected.length} ${customCaptions?.department || 'Department'}(s) selected`;
  };

  return (
    <Grid item lg={3} md={6} xs={12}>
      <DyanamicDropdown
        dataSource={departments}
        columns={[{ name: 'name', id: 1 }]}
        showHeader={false}
        width="100%"
        placeholder={getPlaceholder()}
        onChange={handleChange}
        selectdValueToshow="name"
        selectedValue={multiSelect ? undefined : filterValues[filterKey]?.value}
        selectedValues={multiSelect ? filterValues[filterKey]?.value : undefined}
        label={`Filter by ${customCaptions?.department || 'Department'}`}
        icon={<FilterListIcon style={{ color: 'rgb(48,122,215)' }} />}
        required={false}
        showBorderColor
        multiSelect={multiSelect}
        maxDisplayItems={2}
        showSearchBox={true}
      />
    </Grid>
  );
}; 
